import { Theme } from '@material-ui/core';

type GetFocusVisibleStyle = (
  theme: Theme,
  options?: { inset?: boolean; color?: string },
) => any;

const THICKNESS = 2;

/**
 * Returns the color from the theme to be used for focus outlines.
 *
 * This function exists to ensure consistency in focus styles across the application.
 * By centralizing the focus color logic, it helps maintain a uniform and accessible
 * focus indicator that aligns with the design system and theme.
 *
 * @param theme - The Material-UI theme object.
 * @returns The color value to use for focus outlines in most cases.
 */
export const getFocusColorFromTheme = (theme: Theme) =>
  theme.palette.accent.main;

/**
 * Returns the style object for a focus-visible outline, ensuring accessibility and theme consistency.
 *
 * @param theme - The Material-UI theme object.
 * @param options - Optional settings for customizing the focus outline.
 * @param options.color - Override the default focus color. Only use this when the standard focus color would fail contrast checks with the background.
 * @param options.inset - If true, the outline is drawn inside the element. Override this only when the adjacent inside of the element would not achieve sufficient contrast with the outline.
 * @returns A style object for focus-visible outlines.
 */
export const getFocusVisibleStyle: GetFocusVisibleStyle = (theme, options) => ({
  outline: `solid ${THICKNESS}px`,
  outlineColor: options?.color ?? getFocusColorFromTheme(theme),
  outlineOffset: options?.inset ?? true ? `-${THICKNESS}px` : `${THICKNESS}px`,
});
